//
// # Parasitic Extraction Data Schema
//
// Schema for persisted parasitic extraction results.
// These structures are stored in the depot alongside layout data
// to avoid recomputing R/C values on every design load.
//

/**
 * Persisted parasitic extraction data for a design.
 * Stored as a separate file in the depot: designs/{designId}/parasitics.json
 */
export interface PersistedParasitics {
  /**
   * Schema version for forward/backward compatibility.
   * @type int
   */
  version: number;

  /**
   * ISO timestamp when extraction was performed.
   */
  extractedAt: string;

  /**
   * PDK identifier used for extraction.
   * Used to detect if PDK changed (requires re-extraction).
   */
  pdk: string;

  /**
   * Stack identifier used for extraction.
   * Used to detect if stack changed (requires re-extraction).
   */
  stack: string;

  /**
   * Hash of layout routes at extraction time.
   * Used to detect if layout changed (requires re-extraction).
   * Computed from route IDs and geometries.
   */
  layoutRoutesHash: string;

  /**
   * Extracted data for each wire segment.
   */
  segments: Array<PersistedSegment>;

  /**
   * Extracted data for each via.
   */
  vias: Array<PersistedVia>;

  /**
   * Coupling capacitance between segment pairs.
   */
  couplingPairs: Array<PersistedCoupling>;

  /**
   * Pre-aggregated totals per net for fast queries.
   */
  netTotals: Array<PersistedNetTotals>;
}

/**
 * Persisted extraction data for a wire segment.
 */
export interface PersistedSegment {
  /**
   * Unique segment identifier (e.g., "route_123_seg_2").
   */
  id: string;

  /**
   * Net name this segment belongs to.
   */
  net: string;

  /**
   * Route ID this segment belongs to.
   */
  routeId: string;

  /**
   * Segment index within the route.
   * @type int
   */
  segmentIndex: number;

  /**
   * Layer ID for this segment.
   * @type int
   */
  layer: number;

  /**
   * Segment resistance in milliohms (mΩ).
   */
  resistance: number;

  /**
   * Ground/substrate capacitance in femtofarads (fF).
   */
  groundCapacitance: number;
}

/**
 * Persisted extraction data for a via.
 */
export interface PersistedVia {
  /**
   * Unique via identifier (e.g., "route_123_via_1").
   */
  id: string;

  /**
   * Net name this via belongs to.
   */
  net: string;

  /**
   * Route ID this via belongs to.
   */
  routeId: string;

  /**
   * Number of vias in the via array.
   * @type int
   */
  viaCount: number;

  /**
   * Lower layer ID.
   * @type int
   */
  lowerLayer: number;

  /**
   * Upper layer ID.
   * @type int
   */
  upperLayer: number;

  /**
   * Via resistance in milliohms (mΩ).
   * For via arrays: resistance = resistancePerVia / viaCount
   */
  resistance: number;
}

/**
 * Persisted coupling capacitance between two segments.
 */
export interface PersistedCoupling {
  /**
   * ID of first segment in the coupling pair.
   */
  segmentA: string;

  /**
   * ID of second segment in the coupling pair.
   */
  segmentB: string;

  /**
   * Net of segment A.
   */
  netA: string;

  /**
   * Net of segment B.
   */
  netB: string;

  /**
   * Coupling capacitance in femtofarads (fF).
   */
  capacitance: number;
}

/**
 * Pre-aggregated parasitic totals for a net.
 * Computed from individual segment/via values for fast queries.
 */
export interface PersistedNetTotals {
  /**
   * Net name.
   */
  net: string;

  /**
   * Total wire resistance in milliohms (mΩ).
   */
  totalWireResistance: number;

  /**
   * Total via resistance in milliohms (mΩ).
   */
  totalViaResistance: number;

  /**
   * Total ground/substrate capacitance in femtofarads (fF).
   */
  totalGroundCapacitance: number;

  /**
   * Total coupling capacitance to other nets in femtofarads (fF).
   */
  totalCouplingCapacitance: number;

  /**
   * Number of segments in this net.
   * @type int
   */
  segmentCount: number;

  /**
   * Number of vias in this net.
   * @type int
   */
  viaCount: number;
}

/**
 * Current schema version for PersistedParasitics.
 * Increment when making breaking changes to the schema.
 */
export const PARASITICS_SCHEMA_VERSION = 1;
